import React, { useMemo } from "react";
import { AbsoluteFill, Audio, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import {
  TransitionSeries,
  springTiming,
} from "@remotion/transitions";
import { fade } from "@remotion/transitions/fade";
import { slide } from "@remotion/transitions/slide";
import { wipe } from "@remotion/transitions/wipe";
import { dissolve } from "@remotion/transitions/dissolve";
import { resolvePreset } from "../../presets";
import SmashText from "../scenes/SmashText";
import SplitCompare from "../scenes/SplitCompare";
import StepList from "../scenes/StepList";
import TweetCard from "../scenes/TweetCard";
import CaptionRow from "../scenes/CaptionRow";
import AutoCaption from "../scenes/AutoCaption";
import ImageShow from "../scenes/ImageShow";
import VisualEffect from "../scenes/VisualEffect";
import TimedSubtitle from "../scenes/TimedSubtitle";
import VideoBackground from "../scenes/VideoBackground";
import ModelCard from "../scenes/ModelCard";
import Badge from "../scenes/Badge";
import type {
  BgmConfig,
  LayerConfig,
  SceneConfig,
  TransitionConfig,
} from "../../lib/types";

/** 组件映射表 */
const COMPONENT_MAP: Record<string, React.FC<any>> = {
  SmashText,
  SplitCompare,
  StepList,
  TweetCard,
  CaptionRow,
  AutoCaption,
  ImageShow,
  VisualEffect,
  ModelCard,
  Badge,
  TimedSubtitle,
  VideoBackground,
};

export { COMPONENT_MAP };

export interface EngineProps {
  scenes: SceneConfig[];
  bgm?: BgmConfig;
}

/**
 * 将单层快捷写法（preset + props）转为 layers 数组
 */
function normalizeSceneToLayers(scene: SceneConfig): LayerConfig[] {
  if (scene.layers) return scene.layers;
  if (scene.preset) {
    const component = scene.preset.split(".")[0];
    const preset = scene.preset.split(".").slice(1).join(".");
    return [{ component, preset, props: scene.props }];
  }
  return [];
}

/** resolve 过渡 presentation */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolvePresentation(t: TransitionConfig): any {
  switch (t.type) {
    case "fade":
      return fade();
    case "slide":
      return slide({ direction: t.direction ?? "from-left" });
    case "wipe":
      return wipe({ direction: t.direction ?? "from-left" });
    case "dissolve":
      return dissolve({});
    default:
      return fade();
  }
}

/**
 * 延迟包装器：在 offset 帧之前隐藏内容
 */
const DelayWrapper: React.FC<{ offset: number; children: React.ReactNode }> = ({
  offset,
  children,
}) => {
  const frame = useCurrentFrame();
  if (frame < offset) return null;
  return <>{children}</>;
};

/** 渲染单个场景的所有层 */
function renderLayers(layers: LayerConfig[], sceneDuration: number) {
  return layers.map((layer, i) => {
    const Component = COMPONENT_MAP[layer.component];
    if (!Component) return null;

    let style = {};
    if (layer.preset) {
      const resolved = resolvePreset(`${layer.component}.${layer.preset}`);
      if (resolved) style = resolved;
    }

    // 如果配置了 offset，用 DelayWrapper 延迟渲染
    if (layer.offset && layer.offset > 0) {
      return (
        <DelayWrapper key={`${layer.component}-${i}`} offset={layer.offset}>
          <Component
            {...(layer.props ?? {})}
            style={{ ...style, ...(layer.props?.style ?? {}) }}
            durationInFrames={sceneDuration}
          />
        </DelayWrapper>
      );
    }

    return (
      <Component
        key={`${layer.component}-${i}`}
        {...(layer.props ?? {})}
        style={{ ...style, ...(layer.props?.style ?? {}) }}
        durationInFrames={sceneDuration}
      />
    );
  });
}

const VideoEngine: React.FC<Record<string, unknown>> = (props) => {
  const { scenes, bgm } = props as unknown as EngineProps;
  void useVideoConfig();

  // 标准化 layers
  const normalizedScenes = useMemo(
    () =>
      scenes.map((scene) => ({
        ...scene,
        layers: normalizeSceneToLayers(scene),
      })),
    [scenes],
  );

  // 总帧数（用于 BGM 淡入淡出）
  const totalFrames = useMemo(
    () => scenes.reduce((sum, s) => sum + s.durationInFrames, 0),
    [scenes],
  );

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* BGM — 全局背景音乐 */}
      {bgm && (
        <Audio
          src={staticFile(bgm.src)}
          loop={bgm.loop ?? true}
          volume={(f: number) => {
            const vol = bgm.volume ?? 0.15;
            const fadeIn = 30;
            const fadeOut = 30;
            if (f < fadeIn) return vol * (f / fadeIn);
            if (f > totalFrames - fadeOut)
              return vol * ((totalFrames - f) / fadeOut);
            return vol;
          }}
        />
      )}

      {/* ── TransitionSeries：逐场渲染 + 场景间过渡 ── */}
      <TransitionSeries>
        {normalizedScenes.flatMap((scene, index) => {
          if (!scene.layers || scene.layers.length === 0) {
            console.warn(`场景 ${scene.sceneId} 没有有效层`);
            return [];
          }

          const elements: React.ReactElement[] = [];

          // 本场 Sequence
          elements.push(
            <TransitionSeries.Sequence
              key={`seq-${scene.sceneId}`}
              durationInFrames={scene.durationInFrames}
            >
              {/* 每场 TTS 配音 */}
              {scene.audio?.src && (
                <Audio src={staticFile(scene.audio.src)} />
              )}

              {/* 所有层按数组顺序渲染，下标即 z 序 */}
              {renderLayers(scene.layers, scene.durationInFrames)}
            </TransitionSeries.Sequence>,
          );

          // 如果下一个场景配置了 transition，插入过渡
          const nextScene = normalizedScenes[index + 1];
          if (nextScene?.transition && index < normalizedScenes.length - 1) {
            elements.push(
              <TransitionSeries.Transition
                key={`trans-${scene.sceneId}-to-${nextScene.sceneId}`}
                presentation={resolvePresentation(nextScene.transition)}
                timing={springTiming({
                  durationInFrames: nextScene.transition.durationInFrames,
                })}
              />,
            );
          }

          return elements;
        })}
      </TransitionSeries>
    </AbsoluteFill>
  );
};

export default VideoEngine;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
